'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import MenuItem, { MenuItemData } from './MenuItem';
import ComboBuilder from './ComboBuilder';
import CartSidebar from './CartSidebar';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  alt: string;
}

const InteractiveMenuContent = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isComboBuilderOpen, setIsComboBuilderOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const menuItems: MenuItemData[] = [
  {
    id: 'item-1',
    name: 'Classic Broast Chicken',
    description: 'Our signature crispy broasted chicken marinated in secret spices, pressure-cooked to perfection. Served with garlic mayo and coleslaw.',
    price: 320,
    image: "https://images.unsplash.com/photo-1725210338021-aa5dfff76eae",
    alt: 'Golden crispy broasted chicken pieces with herbs on white ceramic plate with side salad',
    category: 'broast',
    prepTime: 20,
    spiceLevel: 'medium',
    dietary: ['halal'],
    rating: 4.8,
    reviewCount: 342,
    calories: 580,
    protein: 45,
    carbs: 28,
    fat: 32,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Chicken', 'Special spice blend', 'Garlic', 'Herbs', 'Buttermilk', 'Flour'],
    customizations: ['Extra spicy', 'Less oil', 'No garlic', 'Extra crispy'],
    isSignature: true,
    isNew: false
  },
  {
    id: 'item-2',
    name: 'Peri Peri Wings',
    description: 'Fiery chicken wings tossed in authentic peri peri sauce with African bird\'s eye chili. Not for the faint-hearted!',
    price: 280,
    image: "https://images.unsplash.com/photo-1609742432038-ce13093a23ea",
    alt: 'Spicy peri peri chicken wings coated in red sauce with sesame seeds on black plate',
    category: 'wings',
    prepTime: 15,
    spiceLevel: 'extra-hot',
    dietary: ['halal', 'gluten-free'],
    rating: 4.6,
    reviewCount: 287,
    calories: 520,
    protein: 38,
    carbs: 22,
    fat: 28,
    allergens: ['Dairy'],
    ingredients: ['Chicken wings', 'Peri peri sauce', 'African chili', 'Lemon', 'Garlic', 'Paprika'],
    customizations: ['Mild version', 'Extra sauce', 'Dry rub only'],
    isSignature: false,
    isNew: true
  },
  {
    id: 'item-3',
    name: 'Arabian Chicken Shawarma',
    description: 'Tender marinated chicken wrapped in fresh pita with tahini sauce, pickles, and crisp vegetables. A Middle Eastern classic.',
    price: 180,
    image: "https://images.unsplash.com/photo-1598806243937-2072d39bc11d",
    alt: 'Traditional Arabian chicken shawarma wrap with grilled chicken and fresh vegetables on wooden board',
    category: 'arabian',
    prepTime: 12,
    spiceLevel: 'mild',
    dietary: ['halal', 'dairy-free'],
    rating: 4.7,
    reviewCount: 456,
    calories: 420,
    protein: 32,
    carbs: 45,
    fat: 18,
    allergens: ['Gluten', 'Sesame'],
    ingredients: ['Chicken', 'Pita bread', 'Tahini', 'Pickles', 'Tomatoes', 'Lettuce', 'Onions'],
    customizations: ['Extra tahini', 'No pickles', 'Add cheese', 'Extra veggies'],
    isSignature: true,
    isNew: false
  },
  {
    id: 'item-4',
    name: 'Honey Garlic Wings',
    description: 'Sweet and savory wings glazed with honey and roasted garlic. Perfect balance of flavors that keeps you coming back.',
    price: 260,
    image: "https://images.unsplash.com/photo-1594309208645-031ae032e2df",
    alt: 'Glazed honey garlic chicken wings with golden brown coating and sesame seeds on white plate',
    category: 'wings',
    prepTime: 18,
    spiceLevel: 'mild',
    dietary: ['halal'],
    rating: 4.9,
    reviewCount: 523,
    calories: 480,
    protein: 35,
    carbs: 38,
    fat: 22,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Chicken wings', 'Honey', 'Garlic', 'Soy sauce', 'Ginger', 'Sesame seeds'],
    customizations: ['Extra honey', 'Less garlic', 'Crispy style'],
    isSignature: true,
    isNew: false
  },
  {
    id: 'item-5',
    name: 'Spicy Broast Meal',
    description: 'Four pieces of extra spicy broasted chicken with fries, coleslaw, and garlic bread. A complete meal for spice lovers.',
    price: 420,
    image: "https://images.unsplash.com/photo-1672856399624-61b47d70d339",
    alt: 'Complete spicy broast meal with four chicken pieces, french fries, coleslaw and garlic bread on wooden platter',
    category: 'broast',
    prepTime: 25,
    spiceLevel: 'hot',
    dietary: ['halal'],
    rating: 4.5,
    reviewCount: 198,
    calories: 920,
    protein: 58,
    carbs: 72,
    fat: 48,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Broasted chicken', 'French fries', 'Coleslaw', 'Garlic bread', 'Hot spices'],
    customizations: ['Extra fries', 'No coleslaw', 'Extra bread'],
    isSignature: false,
    isNew: false
  },
  {
    id: 'item-6',
    name: 'Falafel Wrap',
    description: 'Crispy chickpea falafel balls with fresh vegetables, tahini sauce, and pickles in warm pita bread. Vegetarian delight!',
    price: 150,
    image: "https://images.unsplash.com/photo-1680990999782-ba7fe26e4d0b",
    alt: 'Vegetarian falafel wrap with crispy chickpea balls, fresh vegetables and tahini sauce in pita bread',
    category: 'arabian',
    prepTime: 10,
    spiceLevel: 'mild',
    dietary: ['halal', 'gluten-free', 'dairy-free', 'nut-free'],
    rating: 4.4,
    reviewCount: 234,
    calories: 380,
    protein: 18,
    carbs: 52,
    fat: 14,
    allergens: ['Sesame'],
    ingredients: ['Chickpeas', 'Pita bread', 'Tahini', 'Vegetables', 'Herbs', 'Spices'],
    customizations: ['Extra falafel', 'No tahini', 'Add hummus'],
    isSignature: false,
    isNew: false
  },
  {
    id: 'item-7',
    name: 'BBQ Chicken Wings',
    description: 'Smoky barbecue wings with tangy sauce and a hint of sweetness. Grilled to perfection with charred edges.',
    price: 270,
    image: "https://images.unsplash.com/photo-1612443016520-314f09b3d449",
    alt: 'Smoky BBQ chicken wings with charred edges and barbecue sauce glaze on dark slate plate',
    category: 'wings',
    prepTime: 20,
    spiceLevel: 'medium',
    dietary: ['halal'],
    rating: 4.7,
    reviewCount: 389,
    calories: 510,
    protein: 40,
    carbs: 32,
    fat: 26,
    allergens: ['Gluten'],
    ingredients: ['Chicken wings', 'BBQ sauce', 'Smoked paprika', 'Brown sugar', 'Vinegar'],
    customizations: ['Extra sauce', 'Less sweet', 'Extra smoky'],
    isSignature: false,
    isNew: false
  },
  {
    id: 'item-8',
    name: 'Chicken Tikka Roll',
    description: 'Tandoori-style chicken tikka wrapped in paratha with mint chutney, onions, and fresh coriander. Indian fusion at its best.',
    price: 190,
    image: "https://images.unsplash.com/photo-1588134431240-c10f5cffa4ea",
    alt: 'Indian chicken tikka roll with tandoori chicken pieces wrapped in paratha with mint chutney and onions',
    category: 'arabian',
    prepTime: 15,
    spiceLevel: 'medium',
    dietary: ['halal'],
    rating: 4.6,
    reviewCount: 312,
    calories: 450,
    protein: 35,
    carbs: 48,
    fat: 20,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Chicken tikka', 'Paratha', 'Mint chutney', 'Onions', 'Coriander', 'Yogurt marinade'],
    customizations: ['Extra tikka', 'No onions', 'Extra chutney', 'Less spicy'],
    isSignature: false,
    isNew: true
  },
  {
    id: 'item-9',
    name: 'Family Broast Bucket',
    description: 'Eight pieces of mixed broasted chicken (legs, thighs, wings) with large fries and family-size coleslaw. Perfect for sharing!',
    price: 680,
    image: "https://images.unsplash.com/photo-1611489495412-265c87036ee4",
    alt: 'Large family bucket filled with eight pieces of mixed broasted chicken with french fries and coleslaw containers',
    category: 'broast',
    prepTime: 30,
    spiceLevel: 'medium',
    dietary: ['halal'],
    rating: 4.8,
    reviewCount: 567,
    calories: 1840,
    protein: 112,
    carbs: 128,
    fat: 96,
    allergens: ['Gluten', 'Dairy'],
    ingredients: ['Mixed chicken pieces', 'French fries', 'Coleslaw', 'Special breading', 'Herbs'],
    customizations: ['All legs', 'All wings', 'Extra fries', 'Extra coleslaw'],
    isSignature: true,
    isNew: false
  },
  {
    id: 'item-10',
    name: 'Buffalo Wings',
    description: 'Classic American buffalo wings with blue cheese dip and celery sticks. Tangy, spicy, and absolutely addictive.',
    price: 290,
    image: "https://images.unsplash.com/photo-1709556722349-4ed11c9f8f28",
    alt: 'Classic buffalo chicken wings with orange hot sauce coating served with blue cheese dip and celery sticks',
    category: 'wings',
    prepTime: 16,
    spiceLevel: 'hot',
    dietary: ['halal'],
    rating: 4.5,
    reviewCount: 421,
    calories: 540,
    protein: 42,
    carbs: 18,
    fat: 34,
    allergens: ['Dairy'],
    ingredients: ['Chicken wings', 'Buffalo sauce', 'Blue cheese', 'Celery', 'Hot sauce', 'Butter'],
    customizations: ['Extra hot', 'Ranch instead', 'No celery'],
    isSignature: false,
    isNew: false
  },
  {
    id: 'item-11',
    name: 'Chicken Kebab Platter',
    description: 'Grilled chicken kebabs with hummus, baba ganoush, pita bread, and Mediterranean salad. Authentic Arabian feast.',
    price: 380,
    image: "https://images.unsplash.com/photo-1642693270397-cee356987f64",
    alt: 'Arabian chicken kebab platter with grilled skewers, hummus, baba ganoush, pita bread and Mediterranean salad',
    category: 'arabian',
    prepTime: 22,
    spiceLevel: 'mild',
    dietary: ['halal', 'dairy-free', 'nut-free'],
    rating: 4.9,
    reviewCount: 489,
    calories: 620,
    protein: 52,
    carbs: 58,
    fat: 24,
    allergens: ['Gluten', 'Sesame'],
    ingredients: ['Chicken kebabs', 'Hummus', 'Baba ganoush', 'Pita', 'Salad', 'Tahini'],
    customizations: ['Extra kebabs', 'No hummus', 'Extra pita', 'Add fries'],
    isSignature: true,
    isNew: false
  },
  {
    id: 'item-12',
    name: 'Lemon Pepper Wings',
    description: 'Zesty wings with fresh lemon juice, cracked black pepper, and herbs. Light, refreshing, and full of flavor.',
    price: 250,
    image: "https://images.unsplash.com/photo-1710508774177-7ac2f3492675",
    alt: 'Lemon pepper chicken wings with fresh herbs and cracked black pepper on white ceramic plate',
    category: 'wings',
    prepTime: 14,
    spiceLevel: 'mild',
    dietary: ['halal', 'gluten-free', 'dairy-free'],
    rating: 4.4,
    reviewCount: 267,
    calories: 440,
    protein: 36,
    carbs: 12,
    fat: 28,
    allergens: [],
    ingredients: ['Chicken wings', 'Lemon juice', 'Black pepper', 'Herbs', 'Garlic', 'Olive oil'],
    customizations: ['Extra lemon', 'More pepper', 'Add garlic'],
    isSignature: false,
    isNew: true
  }];


  const handleAddToCart = (item: MenuItemData) => {
    setCartItems((prev) => {
      const existingItem = prev.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prev.map((cartItem) =>
        cartItem.id === item.id ?
        { ...cartItem, quantity: cartItem.quantity + 1 } :
        cartItem
        );
      }
      return [
      ...prev,
      {
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        image: item.image,
        alt: item.alt
      }];

    });
    setIsCartOpen(true);
  };

  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCartItems((prev) =>
    prev.map((item) => item.id === id ? { ...item, quantity } : item)
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleCheckout = () => {
    alert('Checkout functionality will be implemented with payment gateway integration.');
  };

  const handleSaveCombo = (combo: any) => {
    const comboItems: CartItem[] = [];
    if (combo.main) comboItems.push({ ...combo.main, quantity: 1 });
    if (combo.side) comboItems.push({ ...combo.side, quantity: 1 });
    if (combo.drink) comboItems.push({ ...combo.drink, quantity: 1 });
    if (combo.dessert) comboItems.push({ ...combo.dessert, quantity: 1 });

    setCartItems((prev) => [...prev, ...comboItems]);
    setIsCartOpen(true);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:359:6" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="359" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-20%20lg%3Apt-24%22%7D" className="min-h-screen bg-background pt-20 lg:pt-24">
        <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:360:8" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="360" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-8%22%7D" className="container mx-auto px-4 py-8">
          <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:361:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="361" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:362:12" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="362" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:363:12" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="363" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:365:16" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="365" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded%22%7D" key={i} className="h-96 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:375:4" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="375" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-14%20sm%3Apt-16%20md%3Apt-18%20lg%3Apt-20%20xl%3Apt-24%22%7D" className="min-h-screen bg-background pt-14 sm:pt-16 md:pt-18 lg:pt-20 xl:pt-24">
      <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:376:6" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="376" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-2%20xs%3Apx-3%20sm%3Apx-4%20md%3Apx-5%20lg%3Apx-6%20xl%3Apx-8%20py-3%20xs%3Apy-4%20sm%3Apy-6%20md%3Apy-8%22%7D" className="container mx-auto px-2 xs:px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8 py-3 xs:py-4 sm:py-6 md:py-8">
        {/* Page Header */}
        <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:378:8" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="378" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-3%20xs%3Amb-4%20sm%3Amb-6%20md%3Amb-8%22%7D" className="mb-3 xs:mb-4 sm:mb-6 md:mb-8">
          <h1 data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:379:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="379" data-component-file="InteractiveMenuContent.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20font-headline-bold%20text-xl%20xs%3Atext-2xl%20sm%3Atext-3xl%20md%3Atext-4xl%20lg%3Atext-5xl%20text-text-primary%20mb-2%20sm%3Amb-3%20md%3Amb-4%20leading-tight%20break-words%22%2C%22textContent%22%3A%22Interactive%20Menu%22%7D" className="font-headline font-headline-bold text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-text-primary mb-2 sm:mb-3 md:mb-4 leading-tight break-words">
            Interactive Menu
          </h1>
          <p data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:382:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="382" data-component-file="InteractiveMenuContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-xs%20xs%3Atext-sm%20sm%3Atext-base%20md%3Atext-lg%20text-text-secondary%20max-w-3xl%20break-words%20leading-relaxed%22%2C%22textContent%22%3A%22Explore%20our%20delicious%20selection%20of%20broasted%20chicken%2C%20wings%2C%20and%20Arabian%20specialties.%22%7D" className="font-body text-xs xs:text-sm sm:text-base md:text-lg text-text-secondary max-w-3xl break-words leading-relaxed">
            Explore our delicious selection of broasted chicken, wings, and Arabian specialties.
          </p>
        </div>

        {/* Menu Items Grid */}
        <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:388:8" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="388" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20min-w-0%20overflow-hidden%22%7D" className="w-full min-w-0 overflow-hidden">
          {/* Items Grid - Optimized responsive breakpoints */}
          <div data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:390:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="390" data-component-file="InteractiveMenuContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20min-%5B400px%5D%3Agrid-cols-2%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%202xl%3Agrid-cols-5%20gap-2.5%20xs%3Agap-3%20sm%3Agap-4%20md%3Agap-5%20lg%3Agap-6%22%7D" className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5 xs:gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {menuItems.map((item) => (
              <MenuItem data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:392:14" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="392" data-component-file="InteractiveMenuContent.tsx" data-component-name="MenuItem" data-component-content="%7B%22elementName%22%3A%22MenuItem%22%7D" key={item.id} item={item} onAddToCart={handleAddToCart} />
            ))}
          </div>
        </div>
      </div>

      {/* Combo Builder Modal */}
      {isComboBuilderOpen && (
        <ComboBuilder data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:400:8" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="400" data-component-file="InteractiveMenuContent.tsx" data-component-name="ComboBuilder" data-component-content="%7B%22elementName%22%3A%22ComboBuilder%22%7D"
          onClose={() => setIsComboBuilderOpen(false)}
          onSaveCombo={handleSaveCombo}
        />
      )}

      {/* Cart Sidebar */}
      <CartSidebar data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:407:6" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="407" data-component-file="InteractiveMenuContent.tsx" data-component-name="CartSidebar" data-component-content="%7B%22elementName%22%3A%22CartSidebar%22%7D"
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        items={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onCheckout={handleCheckout}
      />

      {/* Floating Cart Button (Mobile) */}
      {cartItems.length > 0 && !isCartOpen && (
        <button data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:418:8" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="418" data-component-file="InteractiveMenuContent.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22fixed%20bottom-6%20right-6%20w-14%20h-14%20bg-cta%20text-cta-foreground%20rounded-full%20shadow-brand-hover%20flex%20items-center%20justify-center%20lg%3Ahidden%20z-40%20animation-pulse-glow%22%7D"
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-cta text-cta-foreground rounded-full shadow-brand-hover flex items-center justify-center lg:hidden z-40 animation-pulse-glow"
        >
          <Icon data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:422:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="422" data-component-file="InteractiveMenuContent.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShoppingCartIcon%22%7D" name="ShoppingCartIcon" size={24} variant="solid" />
          <span data-component-id="src/app/interactive-menu/components/InteractiveMenuContent.tsx:423:10" data-component-path="src/app/interactive-menu/components/InteractiveMenuContent.tsx" data-component-line="423" data-component-file="InteractiveMenuContent.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20-top-2%20-right-2%20w-6%20h-6%20bg-error%20text-error-foreground%20rounded-full%20flex%20items-center%20justify-center%20text-xs%20font-cta%22%7D" className="absolute -top-2 -right-2 w-6 h-6 bg-error text-error-foreground rounded-full flex items-center justify-center text-xs font-cta">
            {cartItems.length}
          </span>
        </button>
      )}
    </div>
  );
};

export default InteractiveMenuContent;